// SECURITY NOTE: Tokens are stored in localStorage as an accepted risk.
// The recommended production approach is httpOnly cookies to eliminate XSS
// token theft. The current localStorage approach relies on Content Security
// Policy (CSP) headers to mitigate XSS. If migrating to cookies, update the
// gateway to set Set-Cookie headers and remove client-side token management.
// See: OWASP Token Storage Cheat Sheet.
import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { apiClient } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import { PERMS } from '../lib/permissions'

interface User {
  userId: number
  username: string
  email: string
  fullName: string
  firstName: string
  lastName: string
  roles: string[]
  facilityIds: number[]
}

// Shape of /auth/profile response after camelCase conversion.
interface ProfileResponse {
  userId: number
  username: string
  email: string
  fullName: string
  firstName: string
  lastName: string
  roles: string[]
  facilityIds: number[]
  mfaEnabled: boolean
}

// Client-side fallback: derive permissions from role names when backend
// does not include a permissions array in the login response.
// Client-side fallback permission map. Used ONLY when the server login
// response does not include a `permissions[]` array. Every code here
// MUST match an actual backend Permission.permission_code (see
// seed_001 + migrations 024/025).
//
// Mapping key: role_code from the Role table, lowercased.
const ROLE_PERMISSION_MAP: Record<string, string[]> = {
  sys_admin: [
    // All 55 permissions — mirrors DB role 1
    PERMS.PATIENT_VIEW, PERMS.PATIENT_CREATE, PERMS.PATIENT_UPDATE, PERMS.PATIENT_DELETE,
    PERMS.ENCOUNTER_VIEW, PERMS.ENCOUNTER_CREATE, PERMS.ENCOUNTER_UPDATE,
    PERMS.ORDER_VIEW, PERMS.ORDER_CREATE, PERMS.ORDER_SIGN, PERMS.ORDER_DISCONTINUE, PERMS.ORDER_COSIGN,
    PERMS.MED_VIEW, PERMS.MED_PRESCRIBE, PERMS.MED_ADMINISTER, PERMS.MED_VERIFY, PERMS.MED_RECONCILE, PERMS.MED_DOUBLE_CHECK,
    PERMS.RESULT_VIEW, PERMS.RESULT_ENTER, PERMS.RESULT_VERIFY, PERMS.RESULT_SIGN,
    PERMS.DOC_VIEW, PERMS.DOC_CREATE, PERMS.DOC_SIGN, PERMS.DOC_COSIGN, PERMS.DOC_AMEND,
    PERMS.BILLING_VIEW, PERMS.BILLING_CREATE, PERMS.BILLING_PAYMENT,
    PERMS.SCHEDULE_VIEW, PERMS.SCHEDULE_MANAGE,
    PERMS.ANALYTICS_VIEW, PERMS.AUDIT_VIEW,
    PERMS.USER_MANAGE, PERMS.SYS_ADMIN_ALL, PERMS.LICENSING_MANAGE,
    PERMS.BREAK_GLASS, PERMS.RESTRICTED_VIEW, PERMS.BULK_EXPORT,
    PERMS.ENCOUNTER_DISCHARGE, PERMS.PATIENT_MERGE, PERMS.VERBAL_ORDER_VERIFY, PERMS.NOTIFICATION_MANAGE,
    PERMS.DIET_ORDER_CREATE, PERMS.CONSENT_MANAGE, PERMS.BLOOD_ADMIN, PERMS.RESTRAINT_ORDER,
    PERMS.DISCHARGE_PLAN, PERMS.REFERRAL_CREATE, PERMS.REFERRAL_VIEW, PERMS.PORTAL_OWN_DATA,
    PERMS.TRANSFER_MANAGE, PERMS.INFECTION_VIEW, PERMS.RESEARCH_MANAGE,
  ],
  // Legacy "admin" alias for the seeded test user
  admin: [
    PERMS.PATIENT_VIEW, PERMS.PATIENT_CREATE, PERMS.PATIENT_UPDATE, PERMS.PATIENT_DELETE,
    PERMS.ENCOUNTER_VIEW, PERMS.ENCOUNTER_CREATE, PERMS.ENCOUNTER_UPDATE,
    PERMS.ORDER_VIEW, PERMS.ORDER_CREATE, PERMS.ORDER_SIGN, PERMS.ORDER_DISCONTINUE, PERMS.ORDER_COSIGN,
    PERMS.MED_VIEW, PERMS.MED_PRESCRIBE, PERMS.MED_ADMINISTER, PERMS.MED_VERIFY, PERMS.MED_RECONCILE, PERMS.MED_DOUBLE_CHECK,
    PERMS.RESULT_VIEW, PERMS.RESULT_ENTER, PERMS.RESULT_VERIFY, PERMS.RESULT_SIGN,
    PERMS.DOC_VIEW, PERMS.DOC_CREATE, PERMS.DOC_SIGN, PERMS.DOC_COSIGN, PERMS.DOC_AMEND,
    PERMS.BILLING_VIEW, PERMS.BILLING_CREATE, PERMS.BILLING_PAYMENT,
    PERMS.SCHEDULE_VIEW, PERMS.SCHEDULE_MANAGE,
    PERMS.ANALYTICS_VIEW, PERMS.AUDIT_VIEW,
    PERMS.USER_MANAGE, PERMS.SYS_ADMIN_ALL, PERMS.LICENSING_MANAGE,
    PERMS.BREAK_GLASS, PERMS.RESTRICTED_VIEW, PERMS.BULK_EXPORT,
    PERMS.ENCOUNTER_DISCHARGE, PERMS.PATIENT_MERGE, PERMS.VERBAL_ORDER_VERIFY, PERMS.NOTIFICATION_MANAGE,
    PERMS.DIET_ORDER_CREATE, PERMS.CONSENT_MANAGE, PERMS.BLOOD_ADMIN, PERMS.RESTRAINT_ORDER,
    PERMS.DISCHARGE_PLAN, PERMS.REFERRAL_CREATE, PERMS.REFERRAL_VIEW,
    PERMS.TRANSFER_MANAGE, PERMS.INFECTION_VIEW, PERMS.RESEARCH_MANAGE,
  ],
  physician: [
    PERMS.PATIENT_VIEW, PERMS.PATIENT_CREATE, PERMS.PATIENT_UPDATE,
    PERMS.ENCOUNTER_VIEW, PERMS.ENCOUNTER_CREATE, PERMS.ENCOUNTER_UPDATE, PERMS.ENCOUNTER_DISCHARGE,
    PERMS.ORDER_VIEW, PERMS.ORDER_CREATE, PERMS.ORDER_SIGN, PERMS.ORDER_DISCONTINUE, PERMS.ORDER_COSIGN,
    PERMS.MED_VIEW, PERMS.MED_PRESCRIBE, PERMS.MED_RECONCILE,
    PERMS.RESULT_VIEW, PERMS.RESULT_SIGN,
    PERMS.DOC_VIEW, PERMS.DOC_CREATE, PERMS.DOC_SIGN, PERMS.DOC_COSIGN, PERMS.DOC_AMEND,
    PERMS.SCHEDULE_VIEW,
    PERMS.BREAK_GLASS, PERMS.VERBAL_ORDER_VERIFY,
    PERMS.CONSENT_MANAGE, PERMS.RESTRAINT_ORDER, PERMS.DISCHARGE_PLAN, PERMS.REFERRAL_CREATE, PERMS.REFERRAL_VIEW,
  ],
  nurse: [
    PERMS.PATIENT_VIEW, PERMS.PATIENT_UPDATE,
    PERMS.ENCOUNTER_VIEW, PERMS.ENCOUNTER_UPDATE,
    PERMS.ORDER_VIEW, PERMS.ORDER_CREATE,
    PERMS.MED_VIEW, PERMS.MED_ADMINISTER, PERMS.MED_RECONCILE, PERMS.MED_DOUBLE_CHECK,
    PERMS.RESULT_VIEW,
    PERMS.DOC_VIEW, PERMS.DOC_CREATE, PERMS.DOC_AMEND,
    PERMS.SCHEDULE_VIEW,
    PERMS.CONSENT_MANAGE, PERMS.BLOOD_ADMIN, PERMS.DISCHARGE_PLAN, PERMS.REFERRAL_VIEW,
  ],
  pharmacist: [
    PERMS.PATIENT_VIEW,
    PERMS.ENCOUNTER_VIEW,
    PERMS.MED_VIEW, PERMS.MED_VERIFY, PERMS.MED_RECONCILE, PERMS.MED_DOUBLE_CHECK,
    PERMS.RESULT_VIEW,
    PERMS.DOC_VIEW,
    PERMS.SCHEDULE_VIEW,
  ],
  lab_tech: [
    PERMS.PATIENT_VIEW,
    PERMS.ENCOUNTER_VIEW,
    PERMS.ORDER_VIEW,
    PERMS.RESULT_VIEW, PERMS.RESULT_ENTER,
    PERMS.DOC_VIEW,
    PERMS.SCHEDULE_VIEW,
  ],
  radiologist: [
    PERMS.PATIENT_VIEW,
    PERMS.ENCOUNTER_VIEW,
    PERMS.RESULT_VIEW, PERMS.RESULT_ENTER, PERMS.RESULT_VERIFY,
    PERMS.DOC_VIEW,
    PERMS.SCHEDULE_VIEW,
  ],
  reg_clerk: [
    PERMS.PATIENT_VIEW, PERMS.PATIENT_CREATE, PERMS.PATIENT_UPDATE,
    PERMS.ENCOUNTER_VIEW, PERMS.ENCOUNTER_CREATE,
    PERMS.CONSENT_MANAGE,
  ],
  billing: [
    PERMS.PATIENT_VIEW,
    PERMS.BILLING_VIEW, PERMS.BILLING_CREATE, PERMS.BILLING_PAYMENT,
  ],
  // Legacy alias
  billing_clerk: [
    PERMS.PATIENT_VIEW,
    PERMS.BILLING_VIEW, PERMS.BILLING_CREATE, PERMS.BILLING_PAYMENT,
  ],
  med_records: [
    PERMS.PATIENT_VIEW,
    PERMS.DOC_VIEW,
  ],
  // Legacy alias
  front_desk: [
    PERMS.PATIENT_VIEW, PERMS.PATIENT_CREATE, PERMS.PATIENT_UPDATE,
    PERMS.ENCOUNTER_VIEW, PERMS.ENCOUNTER_CREATE,
    PERMS.SCHEDULE_VIEW, PERMS.SCHEDULE_MANAGE,
    PERMS.CONSENT_MANAGE,
  ],
  er_physician: [
    PERMS.PATIENT_VIEW, PERMS.PATIENT_CREATE, PERMS.PATIENT_UPDATE,
    PERMS.ENCOUNTER_VIEW, PERMS.ENCOUNTER_CREATE, PERMS.ENCOUNTER_UPDATE, PERMS.ENCOUNTER_DISCHARGE,
    PERMS.ORDER_VIEW, PERMS.ORDER_CREATE, PERMS.ORDER_SIGN, PERMS.ORDER_DISCONTINUE, PERMS.ORDER_COSIGN,
    PERMS.MED_VIEW, PERMS.MED_PRESCRIBE, PERMS.MED_RECONCILE,
    PERMS.RESULT_VIEW, PERMS.RESULT_SIGN,
    PERMS.DOC_VIEW, PERMS.DOC_CREATE, PERMS.DOC_SIGN, PERMS.DOC_COSIGN, PERMS.DOC_AMEND,
    PERMS.SCHEDULE_VIEW,
    PERMS.BREAK_GLASS, PERMS.VERBAL_ORDER_VERIFY,
    PERMS.CONSENT_MANAGE, PERMS.RESTRAINT_ORDER, PERMS.REFERRAL_CREATE,
  ],
  resident: [
    PERMS.PATIENT_VIEW, PERMS.PATIENT_UPDATE,
    PERMS.ENCOUNTER_VIEW, PERMS.ENCOUNTER_CREATE, PERMS.ENCOUNTER_UPDATE,
    PERMS.ORDER_VIEW, PERMS.ORDER_CREATE,
    PERMS.MED_VIEW, PERMS.MED_PRESCRIBE,
    PERMS.RESULT_VIEW,
    PERMS.DOC_VIEW, PERMS.DOC_CREATE, PERMS.DOC_AMEND,
    PERMS.SCHEDULE_VIEW,
    // Denied: ORDER_SIGN, DOC_SIGN, ENCOUNTER_DISCHARGE, RESULT_SIGN
  ],
  np_pa: [
    PERMS.PATIENT_VIEW, PERMS.PATIENT_CREATE, PERMS.PATIENT_UPDATE,
    PERMS.ENCOUNTER_VIEW, PERMS.ENCOUNTER_CREATE, PERMS.ENCOUNTER_UPDATE, PERMS.ENCOUNTER_DISCHARGE,
    PERMS.ORDER_VIEW, PERMS.ORDER_CREATE, PERMS.ORDER_SIGN, PERMS.ORDER_DISCONTINUE,
    PERMS.MED_VIEW, PERMS.MED_PRESCRIBE, PERMS.MED_RECONCILE,
    PERMS.RESULT_VIEW,
    PERMS.DOC_VIEW, PERMS.DOC_CREATE, PERMS.DOC_SIGN, PERMS.DOC_AMEND,
    PERMS.SCHEDULE_VIEW,
    PERMS.DIET_ORDER_CREATE, PERMS.CONSENT_MANAGE, PERMS.RESTRAINT_ORDER, PERMS.REFERRAL_CREATE, PERMS.REFERRAL_VIEW,
  ],
  charge_nurse: [
    PERMS.PATIENT_VIEW, PERMS.PATIENT_UPDATE,
    PERMS.ENCOUNTER_VIEW, PERMS.ENCOUNTER_UPDATE,
    PERMS.ORDER_VIEW, PERMS.ORDER_CREATE,
    PERMS.MED_VIEW, PERMS.MED_ADMINISTER, PERMS.MED_DOUBLE_CHECK,
    PERMS.RESULT_VIEW,
    PERMS.DOC_VIEW, PERMS.DOC_CREATE,
    PERMS.SCHEDULE_VIEW, PERMS.SCHEDULE_MANAGE,
    PERMS.ANALYTICS_VIEW,
    PERMS.BREAK_GLASS, PERMS.BLOOD_ADMIN, PERMS.TRANSFER_MANAGE,
    PERMS.CONSENT_MANAGE, PERMS.DISCHARGE_PLAN, PERMS.REFERRAL_VIEW,
  ],
  him: [
    PERMS.PATIENT_VIEW, PERMS.PATIENT_CREATE, PERMS.PATIENT_UPDATE,
    PERMS.ENCOUNTER_VIEW,
    PERMS.DOC_VIEW,
    PERMS.PATIENT_MERGE, PERMS.AUDIT_VIEW, PERMS.BULK_EXPORT,
  ],
  quality_officer: [
    PERMS.PATIENT_VIEW,
    PERMS.ENCOUNTER_VIEW,
    PERMS.RESULT_VIEW,
    PERMS.DOC_VIEW,
    PERMS.ANALYTICS_VIEW, PERMS.AUDIT_VIEW,
  ],
  social_worker: [
    PERMS.PATIENT_VIEW, PERMS.PATIENT_UPDATE,
    PERMS.ENCOUNTER_VIEW, PERMS.ENCOUNTER_UPDATE,
    PERMS.DOC_VIEW, PERMS.DOC_CREATE,
    PERMS.SCHEDULE_VIEW,
    PERMS.DISCHARGE_PLAN, PERMS.REFERRAL_CREATE, PERMS.REFERRAL_VIEW,
  ],
  transcriptionist: [
    PERMS.PATIENT_VIEW,
    PERMS.DOC_VIEW, PERMS.DOC_CREATE,
  ],
  ext_provider: [
    PERMS.PATIENT_VIEW,
    PERMS.ENCOUNTER_VIEW,
    PERMS.RESULT_VIEW,
    PERMS.DOC_VIEW,
    PERMS.SCHEDULE_VIEW,
  ],
  // Allied Health
  dietitian: [
    PERMS.PATIENT_VIEW,
    PERMS.ENCOUNTER_VIEW,
    PERMS.ORDER_VIEW,
    PERMS.RESULT_VIEW,
    PERMS.DOC_VIEW, PERMS.DOC_CREATE,
    PERMS.MED_RECONCILE,
    PERMS.SCHEDULE_VIEW,
    PERMS.DIET_ORDER_CREATE,
  ],
  resp_therapist: [
    PERMS.PATIENT_VIEW,
    PERMS.ENCOUNTER_VIEW,
    PERMS.ORDER_VIEW, PERMS.ORDER_CREATE,
    PERMS.MED_VIEW,
    PERMS.RESULT_VIEW,
    PERMS.DOC_VIEW, PERMS.DOC_CREATE,
    PERMS.SCHEDULE_VIEW,
  ],
  phys_therapist: [
    PERMS.PATIENT_VIEW,
    PERMS.ENCOUNTER_VIEW,
    PERMS.ORDER_VIEW,
    PERMS.RESULT_VIEW,
    PERMS.DOC_VIEW, PERMS.DOC_CREATE,
    PERMS.SCHEDULE_VIEW,
  ],
  occ_therapist: [
    PERMS.PATIENT_VIEW,
    PERMS.ENCOUNTER_VIEW,
    PERMS.ORDER_VIEW,
    PERMS.RESULT_VIEW,
    PERMS.DOC_VIEW, PERMS.DOC_CREATE,
    PERMS.SCHEDULE_VIEW,
  ],
  speech_therapist: [
    PERMS.PATIENT_VIEW,
    PERMS.ENCOUNTER_VIEW,
    PERMS.ORDER_VIEW,
    PERMS.RESULT_VIEW,
    PERMS.DOC_VIEW, PERMS.DOC_CREATE,
    PERMS.SCHEDULE_VIEW,
  ],
  // Surgical
  surgeon: [
    PERMS.PATIENT_VIEW, PERMS.PATIENT_CREATE, PERMS.PATIENT_UPDATE,
    PERMS.ENCOUNTER_VIEW, PERMS.ENCOUNTER_CREATE, PERMS.ENCOUNTER_UPDATE, PERMS.ENCOUNTER_DISCHARGE,
    PERMS.ORDER_VIEW, PERMS.ORDER_CREATE, PERMS.ORDER_SIGN, PERMS.ORDER_DISCONTINUE, PERMS.ORDER_COSIGN,
    PERMS.MED_VIEW, PERMS.MED_PRESCRIBE, PERMS.MED_RECONCILE,
    PERMS.RESULT_VIEW, PERMS.RESULT_SIGN,
    PERMS.DOC_VIEW, PERMS.DOC_CREATE, PERMS.DOC_SIGN, PERMS.DOC_COSIGN, PERMS.DOC_AMEND,
    PERMS.SCHEDULE_VIEW, PERMS.SCHEDULE_MANAGE,
    PERMS.BREAK_GLASS, PERMS.VERBAL_ORDER_VERIFY, PERMS.CONSENT_MANAGE,
  ],
  anesthesiologist: [
    PERMS.PATIENT_VIEW, PERMS.PATIENT_CREATE, PERMS.PATIENT_UPDATE,
    PERMS.ENCOUNTER_VIEW, PERMS.ENCOUNTER_CREATE, PERMS.ENCOUNTER_UPDATE, PERMS.ENCOUNTER_DISCHARGE,
    PERMS.ORDER_VIEW, PERMS.ORDER_CREATE, PERMS.ORDER_SIGN, PERMS.ORDER_DISCONTINUE, PERMS.ORDER_COSIGN,
    PERMS.MED_VIEW, PERMS.MED_PRESCRIBE, PERMS.MED_RECONCILE,
    PERMS.RESULT_VIEW, PERMS.RESULT_SIGN,
    PERMS.DOC_VIEW, PERMS.DOC_CREATE, PERMS.DOC_SIGN, PERMS.DOC_COSIGN, PERMS.DOC_AMEND,
    PERMS.SCHEDULE_VIEW,
    PERMS.BREAK_GLASS, PERMS.VERBAL_ORDER_VERIFY, PERMS.CONSENT_MANAGE,
  ],
  // APAC-Specific
  midwife: [
    PERMS.PATIENT_VIEW, PERMS.PATIENT_CREATE, PERMS.PATIENT_UPDATE,
    PERMS.ENCOUNTER_VIEW, PERMS.ENCOUNTER_CREATE, PERMS.ENCOUNTER_UPDATE, PERMS.ENCOUNTER_DISCHARGE,
    PERMS.ORDER_VIEW, PERMS.ORDER_CREATE,
    PERMS.MED_VIEW, PERMS.MED_PRESCRIBE,
    PERMS.RESULT_VIEW,
    PERMS.DOC_VIEW, PERMS.DOC_CREATE, PERMS.DOC_SIGN,
    PERMS.SCHEDULE_VIEW, PERMS.SCHEDULE_MANAGE,
  ],
  trad_med: [
    PERMS.PATIENT_VIEW, PERMS.PATIENT_UPDATE,
    PERMS.ENCOUNTER_VIEW, PERMS.ENCOUNTER_CREATE, PERMS.ENCOUNTER_UPDATE,
    PERMS.ORDER_VIEW, PERMS.ORDER_CREATE,
    PERMS.MED_VIEW, PERMS.MED_PRESCRIBE,
    PERMS.RESULT_VIEW,
    PERMS.DOC_VIEW, PERMS.DOC_CREATE, PERMS.DOC_SIGN,
    PERMS.SCHEDULE_VIEW,
  ],
  dentist: [
    PERMS.PATIENT_VIEW, PERMS.PATIENT_UPDATE,
    PERMS.ENCOUNTER_VIEW, PERMS.ENCOUNTER_CREATE, PERMS.ENCOUNTER_UPDATE,
    PERMS.ORDER_VIEW, PERMS.ORDER_CREATE,
    PERMS.MED_VIEW, PERMS.MED_PRESCRIBE,
    PERMS.RESULT_VIEW,
    PERMS.DOC_VIEW, PERMS.DOC_CREATE, PERMS.DOC_SIGN,
    PERMS.SCHEDULE_VIEW,
    PERMS.CONSENT_MANAGE,
  ],
  // Administrative/Support
  it_support: [
    PERMS.SYS_ADMIN_ALL, PERMS.USER_MANAGE, PERMS.AUDIT_VIEW,
  ],
  dept_head: [
    PERMS.PATIENT_VIEW,
    PERMS.ENCOUNTER_VIEW,
    PERMS.ORDER_VIEW,
    PERMS.MED_VIEW,
    PERMS.RESULT_VIEW,
    PERMS.DOC_VIEW,
    PERMS.SCHEDULE_VIEW, PERMS.SCHEDULE_MANAGE,
    PERMS.ANALYTICS_VIEW, PERMS.AUDIT_VIEW,
  ],
  bed_manager: [
    PERMS.PATIENT_VIEW,
    PERMS.ENCOUNTER_VIEW, PERMS.ENCOUNTER_UPDATE,
    PERMS.SCHEDULE_VIEW,
    PERMS.TRANSFER_MANAGE,
  ],
  interpreter: [
    PERMS.PATIENT_VIEW,
  ],
  med_coder: [
    PERMS.PATIENT_VIEW,
    PERMS.ENCOUNTER_VIEW,
    PERMS.RESULT_VIEW,
    PERMS.DOC_VIEW,
    PERMS.BILLING_VIEW,
  ],
  insurance_coord: [
    PERMS.PATIENT_VIEW,
    PERMS.ENCOUNTER_VIEW,
    PERMS.BILLING_VIEW, PERMS.BILLING_CREATE,
    PERMS.SCHEDULE_VIEW,
  ],
  // Patient-Facing
  patient_portal: [
    PERMS.PORTAL_OWN_DATA,
  ],
  caregiver: [
    PERMS.PORTAL_OWN_DATA,
  ],
  // Operational
  infection_ctrl: [
    PERMS.PATIENT_VIEW,
    PERMS.ENCOUNTER_VIEW,
    PERMS.RESULT_VIEW,
    PERMS.DOC_VIEW,
    PERMS.SCHEDULE_VIEW,
    PERMS.ANALYTICS_VIEW,
    PERMS.INFECTION_VIEW,
  ],
  research_coord: [
    PERMS.PATIENT_VIEW,
    PERMS.ENCOUNTER_VIEW,
    PERMS.RESULT_VIEW,
    PERMS.DOC_VIEW, PERMS.DOC_CREATE,
    PERMS.SCHEDULE_VIEW,
    PERMS.RESEARCH_MANAGE,
  ],
}

function derivePermissionsFromRoles(roleNames: string[]): string[] {
  const permissionSet = new Set<string>()
  for (const role of roleNames) {
    const key = role.toLowerCase().replace(/[\s-]/g, '_')
    const perms = ROLE_PERMISSION_MAP[key]
    if (perms) {
      for (const p of perms) permissionSet.add(p)
    }
  }
  return Array.from(permissionSet)
}

// Extract the server-computed permission list from a JWT access token.
// The gateway puts the full, facility-scoped permission set into the
// token's `permissions` claim at login time, so this is the most
// accurate and always-up-to-date source. Falls back to an empty array
// if the token is malformed.
function permissionsFromToken(token: string): string[] {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return []
    // base64url → base64 (JWT uses URL-safe base64 without padding)
    const payloadB64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const pad = payloadB64.length % 4
    const padded = pad === 0 ? payloadB64 : payloadB64 + '='.repeat(4 - pad)
    const payload = JSON.parse(atob(padded))
    return Array.isArray(payload.permissions) ? payload.permissions : []
  } catch {
    return []
  }
}

interface AuthContextType {
  user: User | null
  login: (username: string, password: string, facilityId: number) => Promise<void>
  logout: () => Promise<void>
  isAuthenticated: boolean
  isLoading: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Populate local state + Zustand store from a /auth/profile response.
  // Shared by mount-time session check and post-login flow so there's
  // one canonical place that decides what "logged-in user" means.
  const hydrateFromProfile = (profile: ProfileResponse, permissions: string[]) => {
    setUser({
      userId: profile.userId,
      username: profile.username,
      email: profile.email,
      fullName: profile.fullName,
      firstName: profile.firstName,
      lastName: profile.lastName,
      roles: profile.roles,
      facilityIds: profile.facilityIds,
    })
    useAuthStore.getState().setUser({
      userId: profile.userId,
      username: profile.username,
      email: profile.email,
      fullName: profile.fullName,
      roleNames: profile.roles,
      facilityIds: profile.facilityIds,
      departmentIds: [],
      isActive: true,
      isMfaEnabled: profile.mfaEnabled,
    })
    useAuthStore.getState().setPermissions(permissions)
  }

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('access_token')
      if (token) {
        try {
          const response = await apiClient.get<ProfileResponse>('/auth/profile')
          const profile = response.data
          // Prefer permissions from the JWT claim (server-computed,
          // facility-scoped). Fall back to role-name → code mapping
          // only if the token is malformed.
          const tokenPerms = permissionsFromToken(token)
          const permissions = tokenPerms.length > 0
            ? tokenPerms
            : derivePermissionsFromRoles(profile.roles)
          hydrateFromProfile(profile, permissions)
        } catch {
          // Token is invalid, clear it
          localStorage.removeItem('access_token')
          localStorage.removeItem('refresh_token')
          useAuthStore.getState().logout()
        }
      }
      setIsLoading(false)
    }

    checkAuth()
  }, [])

  const login = async (username: string, password: string, facilityId: number) => {
    try {
      const response = await apiClient.post('/auth/login', {
        username,
        password,
        facilityId,
      })

      const { accessToken, refreshToken } = response.data

      localStorage.setItem('access_token', accessToken)
      localStorage.setItem('refresh_token', refreshToken)
      useAuthStore.getState().setTokens(accessToken, refreshToken)

      // Fetch the full profile — the login response only carries
      // token claims (user_id, username, email, roles), not the
      // user's display name. /auth/profile is the source of truth.
      const profileResponse = await apiClient.get<ProfileResponse>('/auth/profile')
      const profile = profileResponse.data

      // Permission source, in order of preference:
      //   1. permissions array on the login response (if the backend
      //      starts returning it)
      //   2. the JWT's `permissions` claim (always populated by the
      //      gateway — this is the canonical server-computed list)
      //   3. client-side role-name → code mapping (last-resort fallback
      //      when the token can't be parsed)
      const serverPermissions: string[] | undefined = response.data.permissions
      const tokenPermissions = permissionsFromToken(accessToken)
      const permissions = (serverPermissions && serverPermissions.length > 0)
        ? serverPermissions
        : tokenPermissions.length > 0
          ? tokenPermissions
          : derivePermissionsFromRoles(profile.roles)

      hydrateFromProfile(profile, permissions)
    } catch {
      throw new Error('Invalid credentials')
    }
  }

  const logout = async () => {
    try {
      await apiClient.post('/auth/logout')
    } catch (error) {
      // Logout anyway even if API call fails
      console.error('Logout error:', error)
    } finally {
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      setUser(null)
      useAuthStore.getState().logout()
    }
  }

  const value = {
    user,
    login,
    logout,
    isAuthenticated: !!user,
    isLoading,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
